import { SettingValue } from '@frontend/setting/types';
import { ExtendedPudoTransaction, TransactionStateName } from '@frontend/transaction/types';

export const BULK_MOVE_DEFAULT_STALE_DAYS = 7;
export const BULK_MOVE_MIN_STALE_DAYS = 0;
export const BULK_MOVE_MAX_STALE_DAYS = 90;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Timestamp used for the stale-days clock: creation for drop-off pending, dropped_off for pick-up pending. */
export const getBulkMoveAgeTimestamp = (transaction: ExtendedPudoTransaction): string | null => {
    if (transaction.state === TransactionStateName.TRANSACTION_DROP_OFF_PENDING) {
        return transaction.creation_timestamp;
    }
    if (transaction.state === TransactionStateName.TRANSACTION_PICK_UP_PENDING) {
        return transaction.dropped_off_timestamp;
    }
    return null;
};

/** Parse functionality setting `stale_transaction_days`; invalid/missing → 7. */
export const resolveStaleTransactionDays = (settingValue: SettingValue | undefined): number => {
    if (typeof settingValue === 'number' && Number.isFinite(settingValue) && settingValue > 0) {
        return Math.floor(settingValue);
    }
    if (typeof settingValue === 'string' && settingValue.trim().length > 0) {
        const parsed = Number(settingValue);
        if (Number.isFinite(parsed) && parsed > 0) {
            return Math.floor(parsed);
        }
    }
    return BULK_MOVE_DEFAULT_STALE_DAYS;
};

export const clampStaleTransactionDays = (days: number): number => Math.min(BULK_MOVE_MAX_STALE_DAYS, Math.max(BULK_MOVE_MIN_STALE_DAYS, Math.floor(days)));

export const isStaleForBulkMove = (transaction: ExtendedPudoTransaction, now: Date = new Date(), staleDays: number = BULK_MOVE_DEFAULT_STALE_DAYS): boolean => {
    const timestamp = getBulkMoveAgeTimestamp(transaction);
    if (!timestamp) return false;
    const ageMs = now.getTime() - new Date(timestamp).getTime();
    return ageMs > staleDays * MS_PER_DAY;
};

export const filterStaleBulkMoveTransactions = (
    transactions: ExtendedPudoTransaction[],
    now: Date = new Date(),
    staleDays: number = BULK_MOVE_DEFAULT_STALE_DAYS
): ExtendedPudoTransaction[] => transactions.filter((transaction) => isStaleForBulkMove(transaction, now, staleDays));
